import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  adminListSupportTickets,
  adminUpdateTicketStatus,
} from "@/lib/support.functions";
import {
  SUPPORT_CATEGORY_LABELS,
  SUPPORT_STATUS_LABELS,
} from "@/components/contact-support-dialog";
import { TicketThreadDialog } from "./settings.support";
import { z } from "zod";

const searchSchema = z.object({ ticket: z.string().optional() });

export const Route = createFileRoute("/_authenticated/admin/support")({
  validateSearch: searchSchema,
  component: AdminSupportPage,
});

const STATUSES = ["all", "open", "in_progress", "waiting_on_user", "resolved", "closed"] as const;

function AdminSupportPage() {
  const search = useSearch({ from: "/_authenticated/admin/support" });
  const qc = useQueryClient();
  const listFn = useServerFn(adminListSupportTickets);
  const updateFn = useServerFn(adminUpdateTicketStatus);
  const [filter, setFilter] = useState<(typeof STATUSES)[number]>("open");
  const [activeId, setActiveId] = useState<string | null>(search.ticket ?? null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support-tickets", filter],
    queryFn: () => listFn({ data: { status: filter } }),
  });

  const statusMutation = useMutation({
    mutationFn: (v: { ticketId: string; status: string }) =>
      updateFn({ data: { ticketId: v.ticketId, status: v.status as any } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      toast.success("Status updated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Support tickets</h2>
          <p className="text-sm text-muted-foreground">All inbound user requests.</p>
        </div>
        <Select value={filter} onValueChange={(v) => setFilter(v as any)}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s === "all" ? "All" : SUPPORT_STATUS_LABELS[s] ?? s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-md border border-border">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading...</div>
        ) : !data?.tickets?.length ? (
          <div className="p-6 text-sm text-muted-foreground">No tickets.</div>
        ) : (
          <ul className="divide-y divide-border">
            {data.tickets.map((t: any) => (
              <li key={t.id} className="flex items-center justify-between gap-3 p-4 hover:bg-muted/30">
                <button
                  className="min-w-0 flex-1 text-left"
                  onClick={() => setActiveId(t.id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{t.subject}</span>
                    {!t.last_message_by_admin && t.status !== "resolved" && t.status !== "closed" && (
                      <Badge className="h-5 text-[10px]">Needs reply</Badge>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t.user?.email ?? t.user_id.slice(0, 8)} ·{" "}
                    {SUPPORT_CATEGORY_LABELS[t.category] ?? t.category} ·{" "}
                    {new Date(t.last_message_at).toLocaleString()}
                  </div>
                </button>
                <Select
                  value={t.status}
                  onValueChange={(v) => statusMutation.mutate({ ticketId: t.id, status: v })}
                >
                  <SelectTrigger className="w-40" onClick={(e) => e.stopPropagation()}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.filter((s) => s !== "all").map((s) => (
                      <SelectItem key={s} value={s}>
                        {SUPPORT_STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </li>
            ))}
          </ul>
        )}
      </div>
      {activeId && (
        <TicketThreadDialog ticketId={activeId} onClose={() => setActiveId(null)} isAdmin />
      )}
    </div>
  );
}
